import React, { useState, useEffect } from 'react';
import { useAuth, useFirebase } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { collection, query, where, orderBy, limit, startAfter, getDocs, getDoc, onSnapshot, doc } from 'firebase/firestore';
import { NavLink } from 'react-router-dom';
import { BookOpen, Search, ChevronDown, Lock } from 'lucide-react';
import ArticleCpdModule from './ArticleCpdModule';

const ArticleSummariesPage = ({ onLoginRequest, onLogCpd }) => {
    const { user } = useAuth();
    const { db } = useFirebase();
    const { subscription } = useSubscription();

    const [articles, setArticles] = useState([]);
    const [cpdLogs, setCpdLogs] = useState([]);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [availableTags, setAvailableTags] = useState([]);
    const [activeTag, setActiveTag] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);

    useEffect(() => {
        if (!db) return;
        const fetchTags = async () => {
            const tagsDocRef = doc(db, 'metadata', 'tags');
            const docSnap = await getDoc(tagsDocRef);
            if (docSnap.exists()) {
                setAvailableTags(docSnap.data().allTags.sort());
            }
        };
        fetchTags();
    }, [db]);
    
    useEffect(() => {
        if (!db || subscription === null) return;
        setArticles([]);
        setLastVisible(null);
        setHasMore(true);

        const articlesRef = collection(db, 'articles');
        let q;
        const articlesLimit = subscription?.isActive ? 10 : 5;

        if (activeSearch) {
            const searchLower = activeSearch.toLowerCase().trim();
            q = query(articlesRef, where('keywords', 'array-contains', searchLower), orderBy('createdAt', 'desc'), limit(articlesLimit));
        } else if (activeTag) {
            q = query(articlesRef, where("tags", "array-contains", activeTag), orderBy('createdAt', 'desc'), limit(articlesLimit));
        } else {
            q = query(articlesRef, orderBy('createdAt', 'desc'), limit(articlesLimit));
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newArticles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setArticles(newArticles);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setHasMore(subscription?.isActive ? snapshot.docs.length >= articlesLimit : false);
        });

        return () => unsubscribe();
    }, [db, activeTag, activeSearch, subscription]);
    
    useEffect(() => {
      if (!db || !user || user.isAnonymous) {
          setCpdLogs([]);
          return;
      }
      const logsRef = collection(db, 'users', user.uid, 'cpdLogs');
      const q = query(logsRef, orderBy('completedAt', 'desc'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
          const fetchedLogs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setCpdLogs(fetchedLogs);
      });
      return () => unsubscribe();
    }, [db, user]);

    const handleLoadMore = async () => {
        if (!db || !lastVisible) return;
        const articlesRef = collection(db, 'articles');
        const articlesLimit = 10;
        let q;
        if (activeSearch) {
            const searchLower = activeSearch.toLowerCase().trim();
            q = query(articlesRef, where('keywords', 'array-contains', searchLower), orderBy('createdAt', 'desc'), startAfter(lastVisible), limit(articlesLimit));
        } else if (activeTag) {
            q = query(articlesRef, where("tags", "array-contains", activeTag), orderBy('createdAt', 'desc'), startAfter(lastVisible), limit(articlesLimit));
        } else {
            q = query(articlesRef, orderBy('createdAt', 'desc'), startAfter(lastVisible), limit(articlesLimit));
        }

        const docSnapshots = await getDocs(q);
        const newArticles = docSnapshots.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setArticles(prev => [...prev, ...newArticles]);
        setLastVisible(docSnapshots.docs[docSnapshots.docs.length - 1]);
        setHasMore(docSnapshots.docs.length >= articlesLimit);
    };
    
    const handleTagClick = (tag) => { setSearchTerm(''); setActiveSearch(''); setActiveTag(tag); setIsTagFilterOpen(false); };
    const handleSearchSubmit = (e) => { e.preventDefault(); setActiveTag(null); setActiveSearch(searchTerm); };

    return (
        <div>
            <div className="text-center mb-8">
                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-2">Latest SABR/SRS Publications</h1>
                <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg">AI-generated summaries of recent articles in stereotactic radiotherapy.</p>
            </div>
            
            <div className="mb-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search article titles..."
                            className="w-full px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700 transition duration-200">
                            <Search size={18} />
                        </button>
                    </form>
                    <div className="relative">
                        <button
                            onClick={() => setIsTagFilterOpen(!isTagFilterOpen)}
                            className="w-full flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-semibold"
                        >
                            <span>{activeTag || 'Filter by Tag'}</span>
                            <ChevronDown size={18} className={`transition-transform ${isTagFilterOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isTagFilterOpen && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                                <button
                                    onClick={() => handleTagClick(null)}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${!activeTag ? 'font-bold text-blue-600' : ''}`}
                                >
                                    All Articles
                                </button>
                                {availableTags.map(tag => (
                                    <button
                                        key={tag}
                                        onClick={() => handleTagClick(tag)}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${activeTag === tag ? 'font-bold text-blue-600' : ''}`}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {subscription && !subscription.isActive && (
                 <div className="mb-6 p-4 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center">
                        <Lock className="text-blue-600 dark:text-blue-300 h-6 w-6 mr-3" />
                        <div>
                            <h4 className="font-bold text-blue-800 dark:text-blue-200">Unlock the Full Archive</h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300">You're viewing the 5 most recent articles. Upgrade to Pro for unlimited access.</p>
                        </div>
                    </div>
                    <NavLink to="/pricing" className="flex-shrink-0 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700 transition duration-200">
                        Upgrade Now
                    </NavLink>
                </div>
            )}

            <div className="space-y-6">
                {articles.length > 0 ? (
                    articles.map(article => (
                        <div key={article.id} className="bg-gray-100 dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-300 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-blue-800 dark:text-blue-400 mb-2">{article.title}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">{article.authors} - <i>{article.journal} ({article.year})</i></p>
                            
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                                <h4 className="font-semibold">Key Findings:</h4>
                                <ul>
                                    {article.summary.map((point, index) => (
                                        <li key={index}>{point}</li>
                                    ))}
                                </ul>
                            </div>

                            <a 
                                href={article.link} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="inline-flex items-center mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700 transition duration-200"
                            >
                                <BookOpen size={18} className="mr-2" />
                                Read Full Article
                            </a>
                            <ArticleCpdModule 
                                article={article}
                                user={user}
                                onLoginRequest={onLoginRequest}
                                onLogCpd={onLogCpd}
                                cpdLog={cpdLogs.find(log => log.articleId === article.id)}
                            />
                        </div>
                    ))
                ) : (
                    <div className="text-center text-gray-500 dark:text-gray-500 py-8">
                        <p>No articles found. Try adjusting your search or filter.</p>
                    </div>
                )}
                {hasMore && (
                    <div className="flex justify-center">
                        <button 
                            onClick={handleLoadMore} 
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700 transition duration-200"
                        >
                            Load More Articles
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArticleSummariesPage;